/**
 * @file src/pages/company-onboarding-wizard/hooks/useOnboardingActions.js
 * @description All side effects for the company onboarding wizard.
 *
 * Responsibilities:
 *  - Company creation with logo handling
 *  - Company selection after creation
 *  - Deprecated invite-code join guard
 *  - Skip setup routing
 *
 * Rules:
 *  - No form state here — all state lives in useOnboardingForm.js
 *  - No JSX
 *  - No direct axios calls
 *  - Navigation only happens here — never in the form hook
 *  - isLoading is derived from mutation/action states — no local create loading state
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { getMyCompanies, selectCompany } from "api/company.api";
import { ROUTES } from "config/routes";
import { invalidateCompanyScopedQueries } from "lib/queryHelpers";
import { useCreateCompany } from "../useCreateCompany";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const extractCompanyId = (response) =>
  response?.company?._id ||
  response?.company?.id ||
  response?.data?.company?._id ||
  response?.data?.company?.id ||
  response?.data?._id ||
  response?.data?.id;

const extractCompanies = (response) =>
  response?.data?.companies || response?.companies || response?.data || [];

const findCompanyByLegalName = (companies, legalName) => {
  if (!Array.isArray(companies) || !legalName) return null;

  return companies.find((company) => {
    const companyRecord = company?.company || company;

    return (
      companyRecord?.legalName === legalName ||
      companyRecord?.name === legalName
    );
  });
};

const getCompanyId = (company) => {
  const companyRecord = company?.company || company;
  return companyRecord?._id || companyRecord?.id;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   formData:           object,
 *   setSubmitError:     (message: string) => void,
 *   clearSavedProgress: () => void,
 * }} options
 */
const useOnboardingActions = ({
  formData,
  setSubmitError,
  clearSavedProgress,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: createCompanyMutation, isLoading: isCreating } =
    useCreateCompany();

  const [isSkipping, setIsSkipping] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  // ── Submit — create company ───────────────────────────────────────────────
  /**
   * Called by index.jsx when the wizard reaches the final step and
   * all validation passes.
   */
  const handleSubmit = useCallback(() => {
    createCompanyMutation(formData, {
      onSuccess: async (response) => {
        try {
          clearSavedProgress();

          const createdCompanyId = extractCompanyId(response);

          if (createdCompanyId) {
            await selectCompany(createdCompanyId);
            invalidateCompanyScopedQueries(queryClient);

            navigate(ROUTES.DASHBOARD, {
              replace: true,
              state: {
                message: `${formData.legalName} has been selected.`,
                type: "success",
              },
            });
            return;
          }

          const companiesResponse = await getMyCompanies();
          const companies = extractCompanies(companiesResponse);
          const createdCompany = findCompanyByLegalName(
            companies,
            formData.legalName,
          );

          const fallbackCompanyId = getCompanyId(createdCompany);

          if (fallbackCompanyId) {
            await selectCompany(fallbackCompanyId);
            invalidateCompanyScopedQueries(queryClient);

            navigate(ROUTES.DASHBOARD, {
              replace: true,
              state: {
                message: `${formData.legalName} has been selected.`,
                type: "success",
              },
            });
            return;
          }

          navigate(ROUTES.COMPANY_SELECT, { replace: true });
        } catch (error) {
          setSubmitError(
            error?.message ||
              "Company was created, but we could not select it. Please choose it from the company selector.",
          );

          navigate(ROUTES.COMPANY_SELECT, { replace: true });
        }
      },
      onError: (error) => {
        setSubmitError(
          error?.message || "Failed to create company. Please try again.",
        );
      },
    });
  }, [
    formData,
    createCompanyMutation,
    queryClient,
    navigate,
    clearSavedProgress,
    setSubmitError,
  ]);

  // ── Join company via invite code ──────────────────────────────────────────
  /**
   * Invite-code joining is deprecated.
   * The current backend flow uses invitation links:
   *   /accept-invite?token=...
   *
   * @throws {Error} — re-thrown so JoinCompanyForm can display it locally
   */
  const handleJoin = useCallback(async () => {
    setIsJoining(true);

    try {
      throw new Error(
        "Joining by invite code is no longer supported. Please use the invitation link sent to your email.",
      );
    } finally {
      setIsJoining(false);
    }
  }, []);

  // ── Skip setup ────────────────────────────────────────────────────────────
  /**
   * Skipping setup should not call a fake API.
   * Send the user to company selection; ProtectedRoute will handle whether
   * they need onboarding or can continue.
   */
  const handleSkip = useCallback(() => {
    setIsSkipping(true);
    clearSavedProgress();
    navigate(ROUTES.COMPANY_SELECT, { replace: true });
    setIsSkipping(false);
  }, [navigate, clearSavedProgress]);

  // ── Derived loading state ─────────────────────────────────────────────────
  const isLoading = isCreating || isSkipping || isJoining;

  const loadingMessage = isCreating
    ? "Creating your company..."
    : isSkipping
      ? "Redirecting..."
      : isJoining
        ? "Joining company..."
        : "";

  return {
    handleSubmit,
    handleJoin,
    handleSkip,
    isLoading,
    isJoining,
    loadingMessage,
  };
};

export default useOnboardingActions;
